// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import geometry from 'myWorld/base/geometry';
import measureToolHtml from 'text!html/measureTool.html';
import 'myWorld/uiComponents/dialog';

/**
 * Options for the Measure Tool plugin.
 * Can be over-ridden by adding these in the measurementTool settings or in the application file.
 * @typedef measureToolPluginOptions
 * @property {string}          [divId]                   Id to be assigned to the measure tool dialog
 * @property {Array<string>}   [lengthUnits]             List of length units (amongst ['m', 'km', 'ft', 'yard', 'mi']) to show in the dialog dropdown
 * @property {string}          [defaultLengthUnit='m']   Length unit that is selected by default in the dialog
 * @property {Array<string>}   [areaUnits]               List of area units amongst(['m^2', 'hectare', 'km^2', 'ft^2', 'yd^2', 'acres', 'mi^2']) to show in the dialog dropdown
 * @property {string}          [defaultAreaUnit='m^2']   Area unit that is selected by default in the dialog
 */
myw.MeasureToolPlugin = myw.Plugin.extend({
    options: {
        defaultLengthUnit: 'm',
        defaultAreaUnit: 'm^2',
        lengthUnits: ['m', 'km', 'ft', 'yd', 'mi'],
        areaUnits: ['m^2', 'hectare', 'km^2', 'ft^2', 'yd^2', 'acres', 'mi^2']
    },

    /**
     * @class Provides measure tool functionality <br/>
     * Adds a button to the toolbar to access a dialog which allows the user to save or manage bookmarks
     * @param  {Application}           owner          The application
     * @param  {measureToolPluginOptions}  options
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);

        //Over-ride the options with any settings specified for the measurementTool
        const unitScaleDefs = myw.config['core.units'];
        this.setOptions(myw.config['core.plugin.measureTool']);
        this.lengthScale = new myw.UnitScale(unitScaleDefs.length);
        this.areaScale = new myw.UnitScale(unitScaleDefs.area);

        this.plugin_name = 'measurement_tool';
        this.measureDialog = this.createDialog(options);

        this.map = this.app.map;

        // latlongs
        this.resetMeasurement();

        const drawOptions = {
            create: {
                polyline: {
                    color: 'red'
                }
            }
        };

        this.geomDraw = new myw.GeomDrawMode(this.map, drawOptions);
        this.geomDraw.setGeomType('LineString');
        this.geomDraw.setEditOverlay(null);

        this.map.on('editable:drawing:start', () => {
            if (this.measureDialog.isOpen()) {
                this.map.on('mousemove', this.updateDimensions, this);
            }
        });

        this.map.on('editable:drawing:end', ev => {
            if (this.measureDialog.isOpen()) {
                this.map.off('mousemove', this.updateDimensions, this);
            }
        });
    },

    /**
     * Resets the distance, area and latLngs properties
     */
    resetMeasurement() {
        this.distance = 0.0;
        this.area = 0.0;
        this.latLngs = [];
    },

    /**
     * Adds the html for the measureTool dialog in the map container
     */
    createDialog() {
        return new myw.MeasureToolDialog(this, { divId: this.options.divId, units: this.options });
    },

    /**
     * Handler for click event on the toolbar button
     * Toggles the measure dialog open and close
     */
    toggleMode() {
        const isDialogOpen = this.measureDialog.isOpen();
        this.measureDialog.toggle(!isDialogOpen);

        if (!isDialogOpen) {
            geometry.init();
            this.map.setInteractionMode(this.geomDraw);
            // listen for draw:click to get the points
            this.map.on('editable:editing', this._updateValues, this);
        }
    },

    /**
     * Releases the event handler to update values of the plugin
     * Closes the geom draw mode
     */
    closeMeasureMode() {
        this.map.off('editable:editing', this._updateValues, this);
        this.map.endCurrentInteractionMode();
        this.geomDraw.setEditOverlay(null);
    },

    /**
     * Calculate the total distance of a polyline
     * @param  {array} currentLatLngs
     */
    calculateDistance(currentLatLngs) {
        this.distance = 0;
        if (currentLatLngs.length > 1) {
            const line = geometry.lineString(currentLatLngs);
            this.distance = line.length();
        }
    },

    /**
     * Calculates the total area encompassed by the polyline points
     * @param  {array} currentLatLngs
     */
    calculateArea(currentLatLngs) {
        const ring = geometry.lineString(currentLatLngs);
        const pol = geometry.polygon([currentLatLngs]);

        this.area = !ring.selfIntersects() ? pol.area() : null;
    },

    /**
     * Gets the current distance calculated
     * @return {Float}
     */
    getDistance() {
        return this.distance;
    },

    /**
     * Gets the current area calculated
     * @return {Float|"undefined"}       If its a valid area, returns the area measured
     */
    getArea() {
        return this.area;
    },

    /**
     * Returns object with currently selected length and area units <br/>
     */
    getState() {
        return {
            defaultLengthUnit: this.measureDialog.lengthUnit,
            defaultAreaUnit: this.measureDialog.areaUnit
        };
    },

    /**
     * Updates the length and area in the dialog on mouse move
     * so you can see the total length and area before placing a point on the map
     */
    updateDimensions(e) {
        if (this.latLngs.length > 0) {
            const latlng = e.latlng;
            const currentLatLngs = _.union(this.latLngs, [latlng]);
            this.calculateDistance(currentLatLngs);
            this.calculateArea(currentLatLngs);
            this.measureDialog.updateFields();
        }
    },

    //called when a change is made to the drawing
    _updateValues(data) {
        this.latLngs = data.layer.getLatLngs();
        if (this.latLngs.length === 0) return; //no need to update if nothing is drawn

        geometry.init().then(() => {
            this.calculateDistance(this.latLngs);
            this.calculateArea(this.latLngs);
            this.measureDialog.updateFields();
        });
    }
});

myw.MeasureToolPlugin.prototype.buttons = {
    dialog: myw.PluginButton.extend({
        id: 'a-measureTool', //for automated tests
        titleMsg: 'toolbar_msg',
        imgSrc: 'images/toolbar/measurement-tool.svg',
        action() {
            this.app.recordFunctionalityAccess('core.toolbar.measure_tool');
            this.owner.toggleMode();
        }
    })
};

myw.MeasureToolDialog = myw.Dialog.extend({
    template: _.template(
        $(measureToolHtml)
            .filter('#measure-tool-template')
            .html()
    ),

    events: {
        'change #measurement-tool-length-units-list': '_changeLengthUnit',
        'change #measurement-tool-area-units-list': '_changeAreaUnit'
    },

    options: {
        autoOpen: false,
        modal: false,
        minWidth: 320,
        width: 'auto',
        resizable: false,
        position: { my: 'center', at: 'top+196', of: window },
        title: '{:measure_title}',
        closeText: '{:close_tooltip}',
        buttons: {
            Clear: {
                text: '{:clear_btn}',
                click() {
                    this.clearDrawing();
                }
            },
            Close: {
                text: '{:close_btn}',
                class: 'right',
                click() {
                    this.close();
                }
            }
        }
    },

    /**
     * @class Creates a dialog to show measured distance and area
     * @param  {MeasureToolPlugin}     owner          Measure tool plugin
     * @param  {object}  options
     * @param  {string}                    options.divId  Id to be assigned to the dialog
     * @constructs
     * @extends {Control}
     */
    initialize(owner, options) {
        this.owner = owner;

        myw.Dialog.prototype.initialize.call(this, options);
        this.$el.prop('id', this.options.divId || 'measurement-tool-brief');

        // Set the default unit as specified in the options units
        const availableLengths = Object.keys(this.owner.lengthScale.unitDef.units);
        this.lengthUnits = this.options.units.lengthUnits.filter(x => availableLengths.includes(x));
        this.lengthUnit = this.options.units.defaultLengthUnit;
        const availableAreas = Object.keys(this.owner.areaScale.unitDef.units);
        this.areaUnits = this.options.units.areaUnits.filter(x => availableAreas.includes(x));
        this.areaUnit = this.options.units.defaultAreaUnit;
        this.render();
    },

    render() {
        this.options.contents = this.template({
            lengthOptions: this._createLengthUnitOptions(),
            areaOptions: this._createAreaUnitOptions()
        });

        myw.Dialog.prototype.render.call(this);
        this.$el
            .dialog('widget')
            .find('.ui-dialog-buttonset')
            .css('float', 'none');
        this.$el.dialog('option', 'close', this.closeDialog.bind(this));

        this.translate(this.$el);
        this.delegateEvents();
        this._setDefaultOptions('length', this.lengthUnit);
        this._setDefaultOptions('area', this.areaUnit);
    },

    /**
     * Creates html options for the length unit dropdown
     * @private
     * @return {string}
     */
    _createLengthUnitOptions() {
        let lengthOptions = '';
        _.each(this.lengthUnits, unit => {
            lengthOptions += `<option value= "${unit}">${unit}</option>`;
        });
        return lengthOptions;
    },

    /**
     * Creates html options for the area unit dropdown
     * @private
     * @return {string}
     */
    _createAreaUnitOptions() {
        let areaOptions = '';
        _.each(this.areaUnits, unit => {
            let unitLabel = unit;
            if (unit.indexOf('^2') > -1) {
                unitLabel = unit.replace('^2', '&#178;');
            }
            areaOptions += `<option value= "${unit}">${unitLabel}</option>`;
        });
        return areaOptions;
    },

    /**
     * Sets the default unit in the units dropdown
     * @param {string} dimension Either "length" or "area"
     * @param {string} unit      The unit that is selected in the dropdown list
     * @private
     */
    _setDefaultOptions(dimension, unit) {
        const unitsList = this.$(`#measurement-tool-${dimension}-units-list`)[0];
        if (!unitsList) return;
        for (let i = 0; i < unitsList.options.length; i++) {
            if (unitsList.options[i].value == unit) {
                unitsList.selectedIndex = i;
                break;
            }
        }
    },

    /**
     * Event listener for length unit change
     * @private
     */
    _changeLengthUnit(ev) {
        this.lengthUnit = $(ev.currentTarget).val();
        this.updateLengthField();
    },

    /**
     * Event listener for area unit change
     * @private
     */
    _changeAreaUnit(ev) {
        this.areaUnit = $(ev.currentTarget).val();
        this.updateAreaField();
    },

    /**
     * Updates the length field according to the distance measured by the owner plugin
     */
    updateLengthField() {
        const distance = this.owner.getDistance();
        const convertedDistance = this.owner.lengthScale
            .convert(distance, 'm', this.lengthUnit)
            .toFixed(2);
        this.$('#measurement-tool-distance').html(convertedDistance);
    },

    /**
     * Updates the area field according to the area measured by the owner plugin
     */
    updateAreaField() {
        const area = this.owner.getArea();
        const convertedArea =
            area === null
                ? this.msg('invalid_area')
                : this.owner.areaScale.convert(area, 'm^2', this.areaUnit).toFixed(2);

        this.$('#measurement-tool-area').html(convertedArea);
    },

    /**
     * Updates the measured distance and area in the dialogs
     */
    updateFields() {
        this.updateLengthField();
        this.updateAreaField();
    },

    /**
     * Clears the drawing from the map and disables geomDraw
     * @private
     */
    _removeDrawing() {
        this.owner.geomDraw.disable();
        this.owner.resetMeasurement();
        this.$('#measurement-tool-distance, #measurement-tool-area').html('0.00');
    },

    /**
     * Checks if the dialog is open.
     * @return {Boolean}
     */
    isOpen() {
        return this.$el.dialog('isOpen');
    },

    /**
     * Toggles the dialog
     * @param  {Boolean} show   Whether to open the dialog or to close it.
     */
    toggle(show) {
        const action = show ? 'open' : 'close';
        this.$el.dialog(action);
    },

    /**
     * Clear all measurement tool info(including markers)
     * Reset length and area
     */
    clearDrawing() {
        this._removeDrawing();
        this.owner.geomDraw.enable(null, 'LineString', false);
    },

    /**
     * Clear the drawing from the map; get out of draw mode
     */
    closeDialog() {
        this._removeDrawing();
        this.owner.closeMeasureMode();
    }
});

export default myw.MeasureToolPlugin;
